// order-domain: ownership=kkren copyPolicy=copy-and-prune-mgb pruneAfter=brand-retirement
// 海快家具规则（2026-09-19 调整警示门槛）：至少一件家具达到 4 才（含）才触发警示级；
// 单件 4 才（含）以上的家具满 5 件（含），或含超大／超重家具，为限制级，只能正式報關。
// 费用、派件方式、稅金代收等一律沿用现有正式報關流程，不因家具件数另收固定费用。
// 超大／超重沿用现有海快一般倉门槛。
import { calcHctCai, getSeShpMethod91VolumeLimitCm, volumeLimitSe } from '@g/utils/parcelUtils'
import { getParcelDisplayName } from './mixedSplit'

export const SE_FURNITURE_LEVELS = Object.freeze({
  NONE: 'none',
  WARN: 'warn',
  LIMIT: 'limit',
})

export const SE_FURNITURE_LIMIT_REASONS = Object.freeze({
  COUNT: 'count',
  OVERSIZE: 'oversize',
})

export const SE_FURNITURE_LIMIT_COUNT = 5
// 警示级与限制级件数共用单件才数门槛（含）：小件家具不触发警示，也不计入 5 件门槛。
export const SE_FURNITURE_COUNT_MIN_CAI = 4
// 超重门槛沿用现有海快一般倉 weightLight 规则（69.5KG），用户 2026-09-07 裁决。
export const SE_FURNITURE_OVERWEIGHT_G = 69500
export const SE_FURNITURE_BOTTOM_LOCK = '請先確認海快風險才能下一步'

export function isOversizeFurnitureParcel (parcel, { jyAddrid, provider } = {}) {
  if (!parcel?.isFurniture) return false
  if (Number(parcel.weight) >= SE_FURNITURE_OVERWEIGHT_G) return true
  return volumeLimitSe(parcel, getSeShpMethod91VolumeLimitCm({
    jyAddrid: parcel.jyAddrid || jyAddrid,
    provider,
  }))
}

// 才数 4 才（含）以上的家具才计入件数。
export function isCountedFurnitureParcel (parcel) {
  if (!parcel?.isFurniture) return false
  return calcHctCai(parcel, false) >= SE_FURNITURE_COUNT_MIN_CAI
}

export function computeSeFurnitureRule ({ parcels = [], jyAddrid, provider } = {}) {
  const list = Array.isArray(parcels) ? parcels : []
  const furnitureParcels = list.filter(parcel => parcel?.isFurniture)
  const oversizeParcels = furnitureParcels.filter(parcel => isOversizeFurnitureParcel(parcel, { jyAddrid, provider }))
  const countedFurnitureParcels = furnitureParcels.filter(isCountedFurnitureParcel)
  const furnitureCount = furnitureParcels.length
  const countedFurnitureCount = countedFurnitureParcels.length
  let level = SE_FURNITURE_LEVELS.NONE
  let reason = ''
  if (furnitureCount > 0) {
    if (oversizeParcels.length) {
      level = SE_FURNITURE_LEVELS.LIMIT
      reason = SE_FURNITURE_LIMIT_REASONS.OVERSIZE
    } else if (countedFurnitureCount >= SE_FURNITURE_LIMIT_COUNT) {
      level = SE_FURNITURE_LEVELS.LIMIT
      reason = SE_FURNITURE_LIMIT_REASONS.COUNT
    } else if (countedFurnitureCount > 0) {
      level = SE_FURNITURE_LEVELS.WARN
    }
  }
  return {
    level,
    reason,
    furnitureCount,
    countedFurnitureCount,
    furnitureParcels,
    countedFurnitureParcels,
    oversizeParcels,
    isLimit: level === SE_FURNITURE_LEVELS.LIMIT,
    // 确认记录只看级别、原因与超大／超重包裹集合，不含件数：同级件数变化不必重新确认。
    key: [level, reason, oversizeParcels.map(parcel => parcel.pclid).join(',')].join(':'),
  }
}

const SE_FURNITURE_LEVEL_RANK = {
  [SE_FURNITURE_LEVELS.NONE]: 0,
  [SE_FURNITURE_LEVELS.WARN]: 1,
  [SE_FURNITURE_LEVELS.LIMIT]: 2,
}

export function parseSeFurnitureAckKey (key) {
  const [level = '', reason = '', pclids = ''] = String(key || '').split(':')
  return { level, reason, oversizePclids: pclids ? pclids.split(',') : [] }
}

// 已确认的风险是否覆盖当前规则：同级件数变化、风险降级（限制级→警示级）沿用原确认；
// 风险升级或新增超大／超重包裹才需要重新确认。
export function isSeFurnitureAckCovering (ackKey, rule) {
  const level = rule?.level || SE_FURNITURE_LEVELS.NONE
  if (level === SE_FURNITURE_LEVELS.NONE) return true
  if (!ackKey) return false
  const acked = parseSeFurnitureAckKey(ackKey)
  if ((SE_FURNITURE_LEVEL_RANK[acked.level] || 0) < (SE_FURNITURE_LEVEL_RANK[level] || 0)) return false
  if (rule.reason === SE_FURNITURE_LIMIT_REASONS.OVERSIZE) {
    const ackedSet = new Set(acked.oversizePclids.map(String))
    return (rule.oversizeParcels || []).every(parcel => ackedSet.has(String(parcel.pclid)))
  }
  return true
}

export function formatOversizeParcelText (parcel) {
  const dims = [parcel?.length, parcel?.width, parcel?.height]
    .map(value => Math.round((Number(value) || 0) / 10))
  const kg = Math.round((Number(parcel?.weight) || 0) / 100) / 10
  return `${getParcelDisplayName(parcel)} ${dims.join('×')} cm・${kg} kg`
}

export function buildSeFurnitureCopy (rule, { confirmed = false } = {}) {
  const level = rule?.level || SE_FURNITURE_LEVELS.NONE
  const base = {
    level,
    confirmed,
    sheetTitle: '選擇海快前請確認',
    confirmText: '確認選擇海快',
    cancelText: '取消',
    bottomLock: SE_FURNITURE_BOTTOM_LOCK,
  }
  if (level === SE_FURNITURE_LEVELS.NONE) {
    return { ...base, staticText: '', sheetText: '', ackLabel: '', doneText: '', declRedText: '' }
  }
  if (rule.reason === SE_FURNITURE_LIMIT_REASONS.OVERSIZE) {
    const oversizeText = rule.oversizeParcels.map(formatOversizeParcelText).join('、')
    return {
      ...base,
      staticText: `含超大／超重家具（${oversizeText}），以海快進口只能正式報關`,
      sheetText: `含超大／超重家具（${oversizeText}），無法通過海快 X 光機，需轉一般倉正式報關，並需逐件填寫裝箱單。家具建議走海運。`,
      ackLabel: '我已了解，仍選擇海快（正式報關）',
      doneText: '已確認・正式報關',
      // 超大／超重沿用申報方式區塊现有的 X 光機红字，这里不再重复。
      declRedText: '',
    }
  }
  if (level === SE_FURNITURE_LEVELS.WARN) {
    return {
      ...base,
      staticText: '本單含家具（海關簽審貨物），以海快進口可能轉正式報關',
      sheetText: '家具屬於海關簽審貨物，以海快進口可能轉正式報關。',
      ackLabel: '我已了解，仍選擇海快',
      doneText: '已確認風險',
      declRedText: '',
    }
  }
  return {
    ...base,
    staticText: `本單家具 ${rule.furnitureCount} 件，以海快進口只能正式報關`,
    sheetText: `家具 ${SE_FURNITURE_LIMIT_COUNT} 件以上，以海快進口需正式報關，並需逐件填寫裝箱單，清關時間較長。家具建議走海運。`,
    ackLabel: '我已了解，仍選擇海快（正式報關）',
    doneText: '已確認・正式報關',
    declRedText: `家具 ${SE_FURNITURE_LIMIT_COUNT} 件以上以海快進口需正式報關，報關費及雜費較簡易報關高，清關時間較長。家具建議走海運!`,
  }
}
